// lib/generation-postprocess.ts
import { blurAlphaChannel } from '#lib/canny';
import {
  getTransformedRectBounds,
  projectStudioLayerPointToLocal,
  resolveStudioLayerTransform,
  type StudioLayerTransform,
} from '#lib/studio-layer-transform';
import type { Layer, Rect } from '#types/types';

interface HighResMaskingLayer {
  imageData: ImageData;
  rect: Rect;
  scale?: number;
  transform: StudioLayerTransform;
}

interface HighResMaskingInput {
  frameRect: Rect;
  targetRect: Rect;
  targetScale: number;
  layers: HighResMaskingLayer[];
}

interface EdgeFadeInput {
  radius: number;
  sides: { top: boolean; right: boolean; bottom: boolean; left: boolean };
}

type GenerationPostprocessJob =
  | { kind: 'blurAlpha'; radius: number }
  | { kind: 'layerFinish'; highRes?: HighResMaskingInput; edgeFade?: EdgeFadeInput };

const HIGH_RES_OCCLUSION_ALPHA_MIN = 250;

const resolveHighResLayerOverlap = (layer: HighResMaskingLayer, frameRect: Rect, targetRect: Rect) => {
  const layerScale = layer.scale && layer.scale > 0 ? layer.scale : 1;
  const layerWidth = layer.imageData.width;
  const layerHeight = layer.imageData.height;

  const layerWorldRect = {
    x: frameRect.x + layer.rect.x,
    y: frameRect.y + layer.rect.y,
    w: layerWidth * layerScale,
    h: layerHeight * layerScale,
  };
  const transformedBounds = getTransformedRectBounds(layerWorldRect, layer.transform);

  const intersectX = Math.max(targetRect.x, transformedBounds.x);
  const intersectY = Math.max(targetRect.y, transformedBounds.y);
  const intersectR = Math.min(targetRect.x + targetRect.w, transformedBounds.x + transformedBounds.w);
  const intersectB = Math.min(targetRect.y + targetRect.h, transformedBounds.y + transformedBounds.h);

  if (intersectX >= intersectR || intersectY >= intersectB) return null;
  return { layerScale, layerWidth, layerHeight, layerWorldRect, intersectX, intersectY, intersectR, intersectB };
};

/** Visible raster layers overlapping the target, in stacking order; the rest cannot change the mask. */
export const selectHighResMaskingLayers = (layers: Layer[], frameRect: Rect, targetRect: Rect): HighResMaskingLayer[] =>
  layers
    .filter((layer): layer is Layer & { imageData: ImageData } => Boolean(layer.visible && layer.imageData))
    .map((layer) => ({
      imageData: layer.imageData,
      rect: layer.rect,
      scale: layer.scale,
      transform: resolveStudioLayerTransform(layer.params),
    }))
    .filter((layer) => resolveHighResLayerOverlap(layer, frameRect, targetRect) !== null);

// In preserve-high-res mode (non-overwrite quick powers), attenuate generated pixels
// over finer underlying content instead of fully punching holes.
const applyHighResMasking = (targetImageData: ImageData, { frameRect, targetRect, targetScale, layers }: HighResMaskingInput) => {
  const width = targetImageData.width;
  const height = targetImageData.height;

  // Scale of the top-most visible pixel at each coordinate; Infinity = no layer covers it yet.
  const resolutionBuffer = new Float32Array(width * height).fill(Infinity);

  const safeTargetScale = targetScale > 0 ? targetScale : 1;

  for (const layer of layers) {
    const overlap = resolveHighResLayerOverlap(layer, frameRect, targetRect);
    if (!overlap) continue;
    const { layerScale, layerWidth, layerHeight, layerWorldRect, intersectX, intersectY, intersectR, intersectB } = overlap;

    const startTx = Math.floor((intersectX - targetRect.x) / safeTargetScale);
    const startTy = Math.floor((intersectY - targetRect.y) / safeTargetScale);
    const endTx = Math.ceil((intersectR - targetRect.x) / safeTargetScale);
    const endTy = Math.ceil((intersectB - targetRect.y) / safeTargetScale);

    const clampedStartTx = Math.max(0, startTx);
    const clampedStartTy = Math.max(0, startTy);
    const clampedEndTx = Math.min(width, endTx);
    const clampedEndTy = Math.min(height, endTy);

    const layerData = layer.imageData.data;

    for (let ty = clampedStartTy; ty < clampedEndTy; ty++) {
      const wy = targetRect.y + (ty + 0.5) * safeTargetScale;
      const rowOffset = ty * width;

      for (let tx = clampedStartTx; tx < clampedEndTx; tx++) {
        // World point at the target pixel centre, then inverse-project it into layer-local space.
        const wx = targetRect.x + (tx + 0.5) * safeTargetScale;
        const localPoint = projectStudioLayerPointToLocal({ x: wx, y: wy }, layerWorldRect, layer.transform);
        const lx = Math.floor(localPoint.x / layerScale);
        const ly = Math.floor(localPoint.y / layerScale);

        if (lx < 0 || lx >= layerWidth || ly < 0 || ly >= layerHeight) continue;

        const layerRowOffset = ly * layerWidth;

        // Only opaque-ish upper content blocks: blended/transparent borders intentionally reveal content below.
        const alphaIndex = (layerRowOffset + lx) * 4 + 3;
        const alpha = layerData[alphaIndex];

        if ((alpha || 0) >= HIGH_RES_OCCLUSION_ALPHA_MIN) {
          resolutionBuffer[rowOffset + tx] = layerScale;
        }
      }
    }
  }

  // Keep only a proportional amount of generated alpha where underlying content is finer:
  // retainedAlpha = underlyingScale / targetScale (clamped to [0, 1]).
  const targetData = targetImageData.data;
  const totalPixels = width * height;

  for (let i = 0; i < totalPixels; i++) {
    const underlyingScale = resolutionBuffer[i] ?? Infinity;
    if (!Number.isFinite(underlyingScale)) continue;

    if (underlyingScale >= safeTargetScale) continue;

    const alphaIndex = i * 4 + 3;
    const currentAlpha = targetData[alphaIndex] || 0;
    const dynamicRetentionFactor = Math.min(1, Math.max(0, underlyingScale / safeTargetScale));
    targetData[alphaIndex] = Math.round(currentAlpha * dynamicRetentionFactor);
  }
};

const applyEdgeFade = (imageData: ImageData, { radius, sides: fadeSides }: EdgeFadeInput) => {
  const { width, height, data } = imageData;
  if (radius <= 0) return;

  const minAlpha = 0;
  const maxAlpha = 255;
  const alphaRange = maxAlpha - minAlpha;

  const getFactor = (dist: number) => {
    const t = Math.min(1, Math.max(0, dist / radius));
    const easedT = Math.sin(t * Math.PI / 2); // outSine easing
    return (minAlpha + alphaRange * easedT) / 255;
  };

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let alphaFactor = 1.0;

      if (fadeSides.left && x < radius) {
        alphaFactor = Math.min(alphaFactor, getFactor(x));
      }
      if (fadeSides.right && x >= width - radius) {
        alphaFactor = Math.min(alphaFactor, getFactor(width - 1 - x));
      }
      if (fadeSides.top && y < radius) {
        alphaFactor = Math.min(alphaFactor, getFactor(y));
      }
      if (fadeSides.bottom && y >= height - radius) {
        alphaFactor = Math.min(alphaFactor, getFactor(height - 1 - y));
      }

      if (alphaFactor < 1.0) {
        const idx = (y * width + x) * 4 + 3;
        data[idx] = Math.floor((data[idx] ?? 0) * alphaFactor);
      }
    }
  }
};

/** layerFinish mutates `target` in place and returns it; blurAlpha returns a new ImageData. */
export const computeGenerationPostprocess = (target: ImageData, job: GenerationPostprocessJob): ImageData => {
  if (job.kind === 'blurAlpha') return blurAlphaChannel(target, job.radius);
  if (job.highRes) applyHighResMasking(target, job.highRes);
  if (job.edgeFade) applyEdgeFade(target, job.edgeFade);
  return target;
};
